import type { AnyHint } from './types';

export type { AnyHint } from './types';

/**
 * Turning a form definition into layout, shared by the thing that collects answers,
 * the thing that shows them back, and the thing that describes them to the case. All
 * read the same hints, so a receipt — or a case description — cannot drift from the
 * form that produced it.
 */

/**
 * Drop the fields whose `visibleWhen` is not satisfied by the given values. A hidden
 * field is not rendered, so the form unregisters it and its value never reaches the
 * payload — "not asked" rather than a `false` nobody chose.
 */
export function visibleFields<H extends { visibleWhen?: { field: string; equals: string } }>(
  fields: readonly H[],
  values: Record<string, unknown>,
): H[] {
  return fields.filter(
    (field) => !field.visibleWhen || values[field.visibleWhen.field] === field.visibleWhen.equals,
  );
}

export interface SectionGroup<H> {
  section: string | undefined;
  fields: H[];
}

/**
 * Fold the flat hint list into consecutive runs that share a `section`, so a form can
 * be drawn as the paper form's bands. Grouping is by *run*, not by name: fields with no
 * section become their own untitled group, and a form that sets none renders exactly as
 * it did before sections existed.
 */
export function groupIntoSections<H extends { section?: string | undefined }>(
  fields: readonly H[],
): SectionGroup<H>[] {
  const groups: SectionGroup<H>[] = [];
  for (const field of fields) {
    const last = groups[groups.length - 1];
    if (last && last.section === field.section) {
      last.fields.push(field);
    } else {
      groups.push({ section: field.section, fields: [field] });
    }
  }
  return groups;
}

/**
 * One answer as a person should read it: the option's label rather than its stored
 * value, Yes/No rather than true/false, a formatted date rather than an ISO string.
 *
 * Returns `undefined` when there is nothing to show — an unanswered optional field or
 * one the user was never asked. Callers use that to drop the row entirely, so a blank
 * can never be mistaken for a deliberate answer. A `list` has no single display value;
 * see {@link answeredSections}.
 */
export function displayValue(hint: AnyHint, value: unknown): string | undefined {
  if (value === undefined || value === null || value === '') return undefined;

  switch (hint.widget) {
    case 'select':
      return (hint.options ?? []).find((option) => option.value === value)?.label ?? String(value);
    case 'checkbox':
      return value ? 'Yes' : 'No';
    case 'date': {
      // A date field is a plain YYYY-MM-DD; parsing it as UTC and formatting in UTC
      // keeps it the day the user picked rather than shifting it across a timezone.
      const parsed = new Date(`${value}T00:00:00Z`);
      return Number.isNaN(parsed.getTime())
        ? String(value)
        : parsed.toLocaleDateString(undefined, { dateStyle: 'long', timeZone: 'UTC' });
    }
    case 'list':
      return undefined;
    default:
      return Array.isArray(value) ? value.map(String).join(', ') : String(value);
  }
}

/** A list's rows as a person reads them: one string per cell, in column order. */
export interface AnsweredTable {
  columns: readonly AnyHint[];
  rows: string[][];
}

/** One line of a receipt: a value, or for a list, a table. Exactly one is set. */
export interface AnsweredRow {
  hint: AnyHint;
  value?: string;
  table?: AnsweredTable;
}

export interface AnsweredSection {
  section: string | undefined;
  rows: AnsweredRow[];
}

function answeredTable(hint: AnyHint, value: unknown): AnsweredTable | undefined {
  if (!Array.isArray(value) || value.length === 0 || !hint.columns) return undefined;
  const columns = hint.columns;
  const rows = value.map((row) =>
    columns.map((column) => {
      const cell = (row as Record<string, unknown>)[column.name];
      return displayValue(column, cell) ?? '';
    }),
  );
  return { columns, rows };
}

/**
 * The sections of `form` that have something to show for `payload`, with each field
 * paired to its readable value — or, for a list, its rows. Sections left with no
 * answers are dropped.
 */
export function answeredSections(
  form: { fields: readonly AnyHint[] },
  payload: Record<string, unknown>,
): AnsweredSection[] {
  return groupIntoSections(visibleFields(form.fields, payload))
    .map((group) => ({
      section: group.section,
      rows: group.fields
        .map((hint): AnsweredRow => {
          if (hint.widget === 'list') {
            const table = answeredTable(hint, payload[hint.name]);
            return table ? { hint, table } : { hint };
          }
          const value = displayValue(hint, payload[hint.name]);
          return value === undefined ? { hint } : { hint, value };
        })
        .filter((row) => row.value !== undefined || row.table !== undefined),
    }))
    .filter((group) => group.rows.length > 0);
}
